import { Directive, ElementRef, HostListener, OnChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CnpjUtils } from '../../shared/utils/cnpj-utils';

@Directive({
  selector: '[appCpfCnpj][formControlName],[appCpfCnpj][formControl],[appCpfCnpj][ngModel]',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: CpfCnpjDirective,
    multi: true,
  }],
})
export class CpfCnpjDirective implements ControlValueAccessor, OnChanges {

  onTouched: any;
  onChange: any;

  constructor(private _element: ElementRef) { }

  ngOnChanges(): void {
    if (this.onChange) {
      this.onChange();
    }
  }

  writeValue(value: any): void {
    if (value) {
      this._element.nativeElement.value = this.format(value);
    } else {
      this._element.nativeElement.value = '';
    }
  }

  private format(value: string): string {
    if (!value) return value;
    const sanitizedValue = this.clean(value);

    if (CnpjUtils.isCnpj(value)) {
      if (sanitizedValue.length <= 2) return sanitizedValue;
      if (sanitizedValue.length <= 5) return sanitizedValue.replace(/(.{2})(.+)/, '$1.$2');
      if (sanitizedValue.length <= 8) return sanitizedValue.replace(/(.{2})(.{3})(.+)/, '$1.$2.$3');
      if (sanitizedValue.length <= 12) return sanitizedValue.replace(/(.{2})(.{3})(.{3})(.+)/, '$1.$2.$3/$4');
      return sanitizedValue.replace(/(.{2})(.{3})(.{3})(.{4})(.{0,2})/, (m, a, b, c, d, e) => `${a}.${b}.${c}/${d}${e ? '-' + e : ''}`);
    }

    if (sanitizedValue.length <= 3) return sanitizedValue;
    if (sanitizedValue.length <= 6) return sanitizedValue.replace(/(\d{3})(\d+)/, '$1.$2');
    if (sanitizedValue.length <= 9) return sanitizedValue.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
    return sanitizedValue.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (m, a, b, c, d) => `${a}.${b}.${c}${d ? '-' + d : ''}`);
  }

  private clean(value: string): string {
    if (!value) return value;

    if (CnpjUtils.isCnpj(value)) {
      return CnpjUtils.sanitize(value) || '';
    }

    return value.replace(/\D/g, '');
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  @HostListener('keyup', ['$event'])
  onKeyup($event: any) {
    $event.target.value = this.format($event.target.value);
    this.changeNgModel($event.target.value);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event) {
    const e = <KeyboardEvent>event;

    if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && (e.ctrlKey || e.metaKey)) ||
      (e.keyCode === 67 && (e.ctrlKey || e.metaKey)) ||
      (e.keyCode === 86 && (e.ctrlKey || e.metaKey)) ||
      (e.keyCode === 88 && (e.ctrlKey || e.metaKey)) ||
      (e.keyCode >= 35 && e.keyCode <= 39)) {
      return;
    }

    const currentValue = event.target.value || '';
    const isCnpjContext = CnpjUtils.isCnpj(currentValue);

    if (isCnpjContext) {
      const isLetterOrDigit = (e.keyCode >= 48 && e.keyCode <= 57) ||
        (e.keyCode >= 65 && e.keyCode <= 90) ||
        (e.keyCode >= 96 && e.keyCode <= 105);
      if (!isLetterOrDigit) {
        e.preventDefault();
      }
    } else {
      if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
        const isLetter = e.keyCode >= 65 && e.keyCode <= 90;
        if (!isLetter) {
          e.preventDefault();
        }
      }
    }

    if (event.target.value && event.target.value.length >= 18) {
      e.preventDefault();
    }
  }

  @HostListener('blur', ['$event'])
  onBlur($event: any) {
    $event.target.value = this.format($event.target.value);
    this.changeNgModel($event.target.value);
    this.onTouched();
  }

  private changeNgModel(value: string) {
    if (value) {
      this.onChange(this.clean(value));
    } else {
      this.onChange('');
    }
  }
}
